/**
 * @fileoverview Component for generating and downloading the microprogramming
 * groups report. The page lets the user pick an academic period (reusing the
 * EDH native period filter) and download an Excel report for that period.
 */

import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Text, Box, Button, toast } from "@foris/avocado-suite";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Header } from "@common/components";
import Layout from "@common/layout/Layout";
import TermSelect from "@modules/vacancies/Groups/term-select/TermSelect";
import { PathParams } from "@edhtypes/general";
import styles from "./microprogrammingGroupsReport.module.scss";

/**
 * MicroprogrammingGroupsReport component - lets users select an academic period
 * and download the microprogramming groups report.
 *
 * Features:
 * - Academic period selection via the native EDH period filter (TermSelect)
 * - Download button disabled until a period is selected
 * - Report download via window.open (the legacy report endpoint does not support
 *   credentialed cross-origin requests, so a fetch-based flow is not viable)
 */
const MicroprogrammingGroupsReport: FC = () => {
  const { origin, scenario, workspace }: PathParams = useParams();
  const history = useHistory();
  const { getMicroprogrammingGroupsReportUrl, homeUrl, adaptedHomeUrl } = useNavigationUrl();

  const { t } = useTranslation();

  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);

  /**
   * Handle navigation back to previous page
   * Uses browser history when available, otherwise redirects to home page
   */
  const handleBack = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push(homeUrl(workspace, scenario, origin));
    }
  };

  /**
   * Handle report download action.
   * Opens the report URL in a new tab so the legacy endpoint serves the file
   * using the active browser session.
   */
  const handleDownloadReport = () => {
    if (!selectedTerm) return;

    toast.success(t("microprogramming-groups-report.snackbar.title"), {
      autoClose: 5000,
    });

    window.open(getMicroprogrammingGroupsReportUrl({ term: selectedTerm }), "_blank");
  };

  return (
    <Layout contextSearch>
      <Header
        breadcrumbItems={[
          {
            title: t("microprogramming-groups-report.breadcrumb.prev"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("microprogramming-groups-report.breadcrumb.current"),
          },
        ]}
        title={t("microprogramming-groups-report.title")}
      />
      <main className={styles.microprogrammingGroupsReport}>
        <Text weight="medium" type="lg">
          {t("microprogramming-groups-report.information-box.title")}
        </Text>
        <Box className={styles.microprogrammingGroupsReport_informationBox}>
          <div className={styles.content}>
            <TermSelect
              label={t("microprogramming-groups-report.information-box.select-period.title")}
              placeholder={t(
                "microprogramming-groups-report.information-box.select-period.placeholder",
              )}
              sendValues={({ term }) => setSelectedTerm(term)}
            />
          </div>
        </Box>
        <div className={styles.microprogrammingGroupsReport_actions}>
          <Button variant="ghost" onClick={handleBack}>
            {t("microprogramming-groups-report.btn-cancel")}
          </Button>

          <Button leftIcon="download" disabled={!selectedTerm} onClick={handleDownloadReport}>
            {t("microprogramming-groups-report.btn-download")}
          </Button>
        </div>
      </main>
    </Layout>
  );
};

export default MicroprogrammingGroupsReport;
